import {
  createApiRef,
  type DiscoveryApi,
  type FetchApi,
} from '@backstage/core-plugin-api';
import {
  serializeManagementTimelineQuery,
  type ActivityCockpitSnapshot,
  type ManagementTimelinePage,
} from '@internal/spassu-code-contract';
import type {
  CockpitArtifactContentQuery,
  CockpitDataSource,
  CockpitSnapshotQuery,
  CockpitTimelineQuery,
} from '@internal/spassu-code-cockpit-ui';
import type { TraceArtifactContentPage } from '../api/types';

export interface FrontierCockpitApi extends CockpitDataSource {}

export const frontierCockpitApiRef = createApiRef<FrontierCockpitApi>({
  id: 'plugin.spassu-code.frontier-cockpit',
});

export class FrontierCockpitApiError extends Error {
  constructor(readonly status: number, readonly code: string, message: string) {
    super(message);
    this.name = 'FrontierCockpitApiError';
  }

  static async from(response: Response): Promise<FrontierCockpitApiError> {
    try {
      const body = await response.json();
      const error = body?.error;
      return new FrontierCockpitApiError(
        response.status,
        typeof error?.code === 'string' ? error.code : 'COCKPIT_REQUEST_FAILED',
        typeof error?.message === 'string'
          ? error.message
          : `Cockpit request failed (HTTP ${response.status}).`,
      );
    } catch {
      return new FrontierCockpitApiError(
        response.status,
        'COCKPIT_REQUEST_FAILED',
        `Cockpit request failed (HTTP ${response.status}).`,
      );
    }
  }
}

export class FrontierCockpitClient implements FrontierCockpitApi {
  constructor(
    private readonly discoveryApi: DiscoveryApi,
    private readonly fetchApi: FetchApi,
  ) {}

  async getSnapshot({
    activityId,
    revision,
    signal,
  }: CockpitSnapshotQuery): Promise<ActivityCockpitSnapshot> {
    return this.get(
      `/activities/${encodeURIComponent(activityId)}/cockpit${query({
        revision,
      })}`,
      signal,
    );
  }

  async getTimeline({
    activityId,
    signal,
    ...queryInput
  }: CockpitTimelineQuery): Promise<ManagementTimelinePage> {
    return this.get(
      `/activities/${encodeURIComponent(activityId)}/cockpit/timeline${query(
        serializeManagementTimelineQuery(queryInput),
      )}`,
      signal,
    );
  }

  async getArtifactContent({
    activityId,
    artifactId,
    offset,
    limit,
    signal,
  }: CockpitArtifactContentQuery): Promise<TraceArtifactContentPage> {
    return this.get(
      `/activities/${encodeURIComponent(
        activityId,
      )}/cockpit/artifacts/${encodeURIComponent(artifactId)}/content${query({
        offset,
        limit,
      })}`,
      signal,
    );
  }

  private async get<T>(path: string, signal?: AbortSignal): Promise<T> {
    const baseUrl = await this.discoveryApi.getBaseUrl('spassu-code');
    const response = await this.fetchApi.fetch(`${baseUrl}${path}`, { signal });
    if (!response.ok) throw await FrontierCockpitApiError.from(response);
    return (await response.json()) as T;
  }
}

function query(values: Record<string, string | number | undefined>): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(values)) {
    if (value !== undefined) params.set(key, String(value));
  }
  const encoded = params.toString();
  return encoded ? `?${encoded}` : '';
}
